import { filter, find } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { useDisplayPreferences, usePostDisplayPreferences } from '@/api/userAccount';

export const UNUSED_LINES_NOTIFICATION_ID = 'unused-telemetry-lines';

export type NotificationContext = undefined | { spotterId: string };

export const useNotifications = () => {
    const mixpanel = useMixpanel();
    const { data: displayPreferences, isPending } = useDisplayPreferences();
    const { mutateAsync: postDisplayPreferences } = usePostDisplayPreferences();

    const dismissNotification = useCallback(
        (notificationId?: string | null, context?: NotificationContext) => {
            if (!notificationId || !displayPreferences) {
                return;
            }
            mixpanel?.track(`Dismiss notification`, { notificationId, ...context });
            if (context && 'spotterId' in context) {
                const { spotterId } = context;
                postDisplayPreferences({
                    ...displayPreferences,
                    dismissedSpotterNotifications: [
                        ...(displayPreferences.dismissedSpotterNotifications ?? []),
                        { notificationId, spotterId },
                    ],
                });
            } else {
                postDisplayPreferences({
                    ...displayPreferences,
                    dismissedNotifications: [
                        ...(displayPreferences.dismissedNotifications ?? []),
                        { notificationId: notificationId },
                    ],
                });
            }
        },
        [displayPreferences, mixpanel, postDisplayPreferences]
    );

    const isNotificationDismissed = useCallback(
        (notificationId?: string | null, context?: NotificationContext) => {
            if (!displayPreferences) {
                return undefined;
            }
            if (context && 'spotterId' in context) {
                const { spotterId } = context;
                return find(displayPreferences?.dismissedSpotterNotifications, {
                    spotterId,
                    notificationId,
                });
            }
            return !!find(displayPreferences.dismissedNotifications, {
                notificationId,
            });
        },
        [displayPreferences]
    );

    const undismissNotification = useCallback(
        (notificationId?: string, { spotterId }: { spotterId?: true | string } = {}) => {
            if (!displayPreferences) {
                return;
            }
            if (spotterId) {
                if (spotterId !== true && !isNotificationDismissed(notificationId, { spotterId })) {
                    // save a POST if we don't need it
                    return;
                }
                return postDisplayPreferences({
                    ...displayPreferences,
                    dismissedSpotterNotifications: filter(
                        displayPreferences.dismissedSpotterNotifications,
                        (pref) =>
                            pref.notificationId !== notificationId ||
                            (spotterId !== true && pref.spotterId !== spotterId)
                    ),
                });
            } else {
                if (!isNotificationDismissed(notificationId) || !displayPreferences) {
                    // save a POST if we don't need it
                    return;
                }
                return postDisplayPreferences({
                    ...displayPreferences,
                    dismissedNotifications: filter(
                        displayPreferences.dismissedNotifications,
                        (pref) => pref.notificationId !== notificationId
                    ),
                });
            }
        },
        [displayPreferences, isNotificationDismissed, postDisplayPreferences]
    );

    return useMemo(
        () => ({
            isPending,
            isNotificationDismissed,
            dismissNotification,
            undismissNotification,
        }),
        [dismissNotification, undismissNotification, isNotificationDismissed, isPending]
    );
};
